/**
 * SupplementRow — canonical supplement card.
 *
 * One supplement = one glass card. Layout:
 *
 *   ┌─────────────────────────────────────────────┐
 *   │  ┌───┐  Title  [dosage]   EVIDENCE   ›       │
 *   │  │img│  Optional description text wraps here │
 *   │  └───┘  across multiple lines if needed.     │
 *   └─────────────────────────────────────────────┘
 *
 * The bottle image sits on the left of the entire text column (title +
 * description), not just the title — matching how Care displays a
 * medication card.
 *
 * Bottle image is picked deterministically from supplementId (or name)
 * so the same supplement always shows the same bottle wherever it
 * appears.
 */
import type { ImageSourcePropType } from "react-native";
import { View, Text, Image, Pressable } from "react-native";
import { ChevronRight } from "lucide-react-native";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { cardSurface } from "@louise/moon-ui";
import { semanticColors, colors, withAlpha } from "@louise/theme/tokens";

const SUPPLEMENT_BOTTLES = [
  require("@louise/moon-ui/assets/images/supplement-bottle.png"),
  require("@louise/moon-ui/assets/images/supplement-bottle-2.png"),
  require("@louise/moon-ui/assets/images/supplement-bottle-3.png"),
] as ImageSourcePropType[];

const PILL_FALLBACK =
  require("@louise/moon-ui/assets/images/pill-capsule.png") as ImageSourcePropType;

function bottleIndex(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % SUPPLEMENT_BOTTLES.length;
}

const BROWN = colors.greyBrown;
const BROWN_BG = withAlpha(colors.greyBrown, 0.1);
const TEXT = semanticColors.text.primary;
const TEXT_LIGHT = semanticColors.text.tertiary;

export type SupplementEvidence = "strong" | "moderate" | "emerging";

type Props = {
  name: string;
  supplementId?: string;
  dosage?: string;
  description?: string;
  evidence?: SupplementEvidence;
  imageSource?: ImageSourcePropType;
  onPress?: () => void;
};

export function SupplementRow({
  name,
  supplementId,
  dosage,
  description,
  imageSource,
  onPress,
}: Props) {
  const capturePress = usePressCapture("SupplementRow");
  const resolvedImage =
    imageSource ??
    SUPPLEMENT_BOTTLES[bottleIndex(supplementId ?? name)] ??
    PILL_FALLBACK;

  return (
    <Pressable
      onPress={
        onPress
          ? () => {
              capturePress(pressLabel(name), "supplement_row");
              onPress();
            }
          : undefined
      }
      disabled={!onPress}
      style={{
        ...cardSurface(),
        overflow: "hidden",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          paddingHorizontal: 16,
          paddingVertical: 12,
        }}
      >
        {/* Left: bottle image, centered on the row alongside the chevron. */}
        <Image
          source={resolvedImage}
          style={{ width: 28, height: 28 }}
          resizeMode="contain"
        />

        {/* Middle: title row + optional description below */}
        <View style={{ flex: 1, gap: 4 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 15, fontWeight: "600", color: TEXT }}
            >
              {name}
            </Text>
            {dosage ? (
              <View
                style={{
                  backgroundColor: BROWN_BG,
                  paddingHorizontal: 8,
                  paddingVertical: 2,
                  borderRadius: 6,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 11, fontWeight: "700", color: BROWN }}
                >
                  {dosage}
                </Text>
              </View>
            ) : null}
          </View>
          {description ? (
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: TEXT_LIGHT, lineHeight: 17 }}
            >
              {description}
            </Text>
          ) : null}
        </View>

        {/* Right: chevron only, centered with the row. Evidence ladder
            lives in the supplement detail sheet, not as a row badge. */}
        {onPress ? (
          <ChevronRight size={14} color={TEXT_LIGHT} strokeWidth={1.5} />
        ) : null}
      </View>
    </Pressable>
  );
}
